import WorkIcon from '@mui/icons-material/Work';
import { Paper, Grid, Chip, Stack, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import React, { FC, useState, useEffect } from 'react';
import { IJobCandidateInteractionResponse, IUserDTO } from 'wisestep-api';

import { timeSince, getDateUsingFormat } from '../../helper/DateOperations/DateOperations';
import WsColors from '../../WsColors';
import Item from '../Item/Item';
import MappingTimeline from '../MappingTimeline/MappingTimeline';
import SubmissionTimeline from '../SubmissionTimeline/SubmissionTimeline';
import WsButton from '../WsButton/WsButton';
import WsDotIcon, { WsDotTextColors, WsDotColors } from '../WsDotIcon/WsDotIcon';
import { WsIconText } from '../WsIconText';
import WsTypography, { WsTypoColors, WsTypoVariants } from '../WsTypography/WsTypography';

interface IActivityCard {
  history: IJobCandidateInteractionResponse | null;
  conversation: IJobCandidateInteractionResponse;
  onUpdateSubmissionStatus?: () => void;
  onActivityUpdate?: () => void;
  onClickOnJobCode?: (args: string) => void;
  loadHistory: (id: string) => void;
  taskLisiting?: JSX.Element;
  isInDnc?: boolean;
  getFieldSettings: (isMapping: boolean) => any;
  fullView?: boolean;
  jobAdminTeam?: boolean;
  loggedInUser?: IUserDTO;
}

const GridItem = styled(Item)(() => ({
  padding: '0 0 10px 0',
}));

type ChipType = {
  textColor: string;
  bgColor?: string;
  fontWeight?: number;
  mt?: string;
};

const StyledChip = styled(Chip)(
  ({ textColor, bgColor = WsColors.TheBlues500, fontWeight = 800, mt = '0px' }: ChipType) => ({
    color: textColor,
    borderRadius: 4,
    fontSize: '14px',
    marginRight: 5,
    marginTop: mt,
    background: bgColor,
    fontWeight: fontWeight,
    height: '22px',
    paddingLeft: 0,
    paddingRight: 0,
  })
);

const ActivityCard: FC<IActivityCard> = ({
  history,
  conversation,
  onUpdateSubmissionStatus,
  onActivityUpdate,
  onClickOnJobCode,
  loadHistory,
  taskLisiting,
  isInDnc = false,
  getFieldSettings,
  fullView = false,
  loggedInUser,
  jobAdminTeam,
}) => {
  const actions = conversation.actions;
  const results = conversation.result;
  const isSubmission = conversation.candidateStatusTags.includes('submitted');
  const workspaceId = conversation.workspaceId === '0ed56aea-54c9-4485-987f-ffb35872eb38';
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [conversationStatus, setConversationStatus] = useState<string>('');
  const [createdUser, setCreatedUser] = useState<boolean>(false);

  useEffect(() => {
    if (fullView && !isLoaded && conversation?.id) {
      loadHistory(conversation.id);
      setShowTimeline(true);
      setIsLoaded(true);
    }
  }, [conversation.id, fullView, loadHistory, isLoaded]);

  const loadMore = (id: string) => {
    loadHistory(id);
    setShowTimeline(true);
  };
  useEffect(() => {
    (async () => {
      if (conversation.id) {
        const emails: string[] = loggedInUser?.email ? [loggedInUser.email] : [];

        if (conversation?.createdBy && emails.indexOf(conversation?.createdBy) > -1) {
          setCreatedUser(true);
        }
      }
    })();
  }, [conversation, loggedInUser]);
  useEffect(() => {
    if (conversation.candidateStatusTags) {
      switch (conversation.candidateStatusTags[0]) {
        case 'mapped':
          setConversationStatus('Mapped');
          break;
        case 'submitted':
          setConversationStatus('Submitted');
          break;
        case 'uploaded':
          setConversationStatus('Uploaded');
          break;
        case 'applied':
          setConversationStatus('Applied');
          break;
      }
    }
  }, [conversation.candidateStatusTags]);

  const validateSubmission = () => {
    onActivityUpdate && onActivityUpdate();
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
      <Grid container direction="row" spacing={2}>
        <Grid container item xs={12} direction="row" spacing={0.3}>
          {conversationStatus !== 'Uploaded' ? (
            <Grid container>
              <Grid item xs={5.8}>
                <Grid container direction="row" justifyContent="flex-start" spacing={0.9}>
                  <Grid item>
                    <GridItem>
                      <WorkIcon sx={{ color: WsColors.TheBlues500, fontSize: '24px' }} />
                    </GridItem>
                  </Grid>
                  <Grid item>
                    <GridItem>
                      <WsTypography>{conversation.mappedJobTitle?.slice(0, 28) ?? ''}</WsTypography>
                    </GridItem>
                  </Grid>
                  <Grid item>
                    <WsIconText
                      text={conversation.jobCode ? `(${conversation.jobCode})` : ''}
                      onClick={() =>
                        onClickOnJobCode && onClickOnJobCode(conversation?.jobCode ?? '')
                      }
                      icon={<></>}
                      popOverText="Open Job Crm"
                      textCursor="pointer"
                      textColor={WsTypoColors.TERTIARY}
                      textSize={WsTypoVariants.SUB_TITLE}
                    />
                  </Grid>
                  <Grid item>
                    <GridItem>
                      <WsTypography>{conversation.companyName ?? ''}</WsTypography>
                    </GridItem>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6.2}>
                <Grid container justifyContent="flex-end">
                  {!isSubmission &&
                    actions?.map((action: string, index: number) => (
                      <StyledChip key={index} label={action} mt="8px" textColor={WsColors.White} />
                    ))}
                  {!isSubmission &&
                    results?.map((action: string, index: number) => (
                      <StyledChip
                        key={index}
                        mt="8px"
                        label={action}
                        textColor={WsColors.White}
                        bgColor={WsColors.Yellow500}
                      />
                    ))}
                  {isSubmission && (
                    <>
                      <StyledChip
                        label={conversation.applicationStage}
                        textColor={WsColors.White}
                      />
                      <StyledChip
                        label={conversation.applicationState}
                        textColor={WsColors.White}
                        bgColor={WsColors.Yellow500}
                      />
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid item>
                <GridItem>
                  <StyledChip
                    label={workspaceId ? 'Viewed' : conversationStatus}
                    size="small"
                    textColor={WsColors.TertiaryShades800}
                    bgColor={WsColors.TheBlues300}
                    fontWeight={500}
                  />
                </GridItem>
              </Grid>
              <Grid item>
                <GridItem>
                  <WsDotIcon
                    color={WsDotColors.SENARY}
                    textColor={WsDotTextColors.SENARY}
                    value={`${workspaceId ? 'Viewed' : 'Uploaded'} by : ${
                      conversation.recruiterName ?? conversation.createdBy
                    } 
                                ${timeSince(String(conversation.createdOn))}`}
                  />
                </GridItem>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1} mt={0.6}>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={6}>
              {conversation?.createdBy == conversation?.modifiedBy ? (
                <WsDotIcon
                  color={WsDotColors.SENARY}
                  textColor={WsDotTextColors.SENARY}
                  popOverText={`${
                    conversation?.createdOn
                      ? getDateUsingFormat(conversation?.createdOn, 'D MMMM YYYY, HH:mm')
                      : ''
                  }`}
                  value={`
                    ${!isSubmission ? 'Mapped by : ' : 'Submitted by :'} 
                    ${conversation.createdBy} 
                    ${timeSince(String(conversation.createdOn))}`}
                />
              ) : (
                <WsDotIcon
                  color={WsDotColors.SENARY}
                  textColor={WsDotTextColors.SENARY}
                  popOverText={`${
                    conversation?.modifiedOn
                      ? getDateUsingFormat(conversation?.modifiedOn, 'D MMMM YYYY, HH:mm')
                      : ''
                  }`}
                  value={`
                  ${'Updated by : '} 
                  ${conversation.modifiedBy} 
                  ${timeSince(String(conversation.modifiedOn))}`}
                />
              )}
            </Grid>
            <Grid item xs={6}>
              {isSubmission && showTimeline ? (
                <Stack direction="row" spacing={0.7} justifyContent="end">
                  {!isInDnc && (
                    <WsButton
                      variant="outlined"
                      size="small"
                      onClick={validateSubmission}
                      disabled={
                        isSubmission ? (createdUser ? false : jobAdminTeam ? false : true) : false
                      }
                    >
                      {`${isSubmission ? 'Edit Submission' : 'Update'}`}
                    </WsButton>
                  )}
                  {!isInDnc && isSubmission && (
                    <WsButton
                      variant="outlined"
                      size="small"
                      id="update_status"
                      onClick={() => onUpdateSubmissionStatus && onUpdateSubmissionStatus()}
                    >
                      Update Status
                    </WsButton>
                  )}
                </Stack>
              ) : (
                <Stack direction="row" justifyContent="end">
                  <StyledChip
                    label={conversationStatus}
                    size="small"
                    textColor={WsColors.TertiaryShades800}
                    bgColor={WsColors.TheBlues300}
                    fontWeight={500}
                  />
                </Stack>
              )}
            </Grid>
          </Grid>
          <Grid item>
            {isSubmission && (!isEmpty(actions) || !isEmpty(results)) && conversation?.createdBy && (
              <WsDotIcon
                color={WsDotColors.SENARY}
                textColor={WsDotTextColors.SENARY}
                value={`Last updated by : 
                      ${conversation?.createdBy} 
                      ${conversation?.createdOn ? timeSince(String(conversation?.createdOn)) : ''}`}
              />
            )}
          </Grid>
        </Grid>
        {!showTimeline && (
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} sx={{ cursor: 'pointer', float: 'right', mt: 1 }}>
              <WsButton
                variant="outlined"
                size="small"
                onClick={() => conversation.id && loadMore(conversation.id)}
              >
                View all
              </WsButton>
              {!isInDnc && (
                <WsButton
                  variant="outlined"
                  size="small"
                  onClick={validateSubmission}
                  disabled={
                    isSubmission ? (createdUser ? false : jobAdminTeam ? false : true) : false
                  }
                >
                  {`${isSubmission ? 'Edit Submission' : 'Update'}`}
                </WsButton>
              )}
              {!isInDnc && isSubmission && (
                <WsButton
                  variant="outlined"
                  size="small"
                  id="update_status"
                  onClick={() => onUpdateSubmissionStatus && onUpdateSubmissionStatus()}
                >
                  Update Status
                </WsButton>
              )}
            </Stack>
          </Grid>
        )}
      </Grid>

      {showTimeline && history && (
        <>
          <Box sx={{ float: 'right' }} mt={2}>
            <WsTypography
              onClick={() => setShowTimeline(false)}
              cursor="pointer"
              color={WsTypoColors.PRIMARY}
            >
              Hide all
            </WsTypography>
          </Box>
          {!isSubmission ? (
            <MappingTimeline
              candidate={history}
              taskLisiting={taskLisiting}
              getFieldSettings={getFieldSettings}
            />
          ) : (
            <SubmissionTimeline
              candidate={history}
              displayAllDetails={true}
              taskLisiting={taskLisiting}
              getFieldSettings={getFieldSettings}
              hideTimeline={true}
            />
          )}
        </>
      )}
    </Paper>
  );
};

export default ActivityCard;
